import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Calendar, ArrowLeft, CheckCircle, Clock, Users, Download, Share2, Heart, ExternalLink, FileText } from 'lucide-react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Breadcrumbs } from '../components/layout/Breadcrumbs';
import { ImageGallery } from '../components/shared/ImageGallery';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  ongoing: { label: 'Ongoing', color: 'bg-cpre-hill text-white', icon: Clock },
  completed: { label: 'Completed', color: 'bg-cpre-new-yew text-white', icon: CheckCircle },
  volunteer_needed: { label: 'Volunteers Needed', color: 'bg-cpre-autumn-leaf text-white', icon: Users },
};

const CPRE_WEB_IMAGES = '/images/cpre images for website';
const projectImage = (filename) => `${CPRE_WEB_IMAGES}/${filename}`;
const projectGallery = (...numbers) => numbers.map((n) => projectImage(`${n}.jpg`));

const FALLBACK_PROJECTS_BY_ID = {
  '1': {
    title: 'Rutland Water Habitat Restoration',
    description: 'A comprehensive habitat restoration project at Rutland Water, focusing on wetland conservation and native species protection. This ongoing initiative involves reed bed management, water quality monitoring, and creating safe nesting sites for migratory birds.\n\nOur team of volunteers and conservation experts work year-round to maintain and improve this vital ecosystem. The project has already seen significant success with increased bird populations and improved water quality.\n\nThe restoration work includes planting native vegetation, removing invasive species, and installing bird nesting platforms. We also conduct regular wildlife surveys to monitor the success of our efforts.',
    location: 'Rutland Water Nature Reserve',
    county: 'Rutland',
    category: 'habitat',
    status: 'ongoing',
    image_url: projectImage('1.jpg'),
    latitude: 52.6575,
    longitude: -0.6478,
    gallery: projectGallery(1, 2, 3, 4, 5),
    timeline: [
      { date: '2024-01', title: 'Project Launch', description: 'Initial assessment and planning completed' },
      { date: '2024-06', title: 'Phase 1 Complete', description: 'Reed bed restoration in northern section' },
      { date: '2025-01', title: 'Volunteer Program Started', description: 'Regular volunteer days established' },
      { date: '2025-06', title: 'Wildlife Survey', description: '50% increase in bird species recorded' },
    ],
    resources: [
      { name: 'Project Overview PDF', url: '#', size: '2.4 MB', type: 'pdf' },
      { name: 'Volunteer Guide', url: '#', size: '1.2 MB', type: 'pdf' },
      { name: 'Wildlife Survey Report', url: '#', size: '3.8 MB', type: 'pdf' },
      { name: 'Site Map', url: '#', size: '1.5 MB', type: 'pdf' },
    ],
    impact: { volunteers: 45, hours: 1200, species: 25 },
  },
  '2': {
    title: 'Lincoln Wolds Trail Enhancement',
    description: 'Improving public access along the Lincoln Wolds with new waymarkers and information boards. This ongoing project makes it easier for walkers and cyclists to explore one of England\'s most beautiful Areas of Outstanding Natural Beauty.\n\nVolunteers help maintain footpaths, install interpretation panels, and ensure trails remain accessible through all seasons. The work connects communities with the landscape while protecting sensitive habitats along the route.\n\nNew wayfinding and visitor information help people discover the Wolds responsibly, supporting local tourism and countryside appreciation.',
    location: 'Lincoln Wolds',
    county: 'Lincolnshire',
    category: 'trails',
    status: 'ongoing',
    image_url: projectImage('6.jpg'),
    latitude: 53.3501,
    longitude: -0.1202,
    gallery: projectGallery(6, 7, 8, 9, 10),
    timeline: [
      { date: '2024-03', title: 'Trail Survey', description: 'Route assessment and priority sections identified' },
      { date: '2024-09', title: 'Waymarkers Installed', description: 'New signage along main Wolds paths' },
      { date: '2025-02', title: 'Information Boards', description: 'Interpretation panels added at key viewpoints' },
    ],
    resources: [
      { name: 'Trail Map PDF', url: '#', size: '1.8 MB', type: 'pdf' },
      { name: 'Volunteer Guide', url: '#', size: '1.2 MB', type: 'pdf' },
    ],
    impact: { volunteers: 32, hours: 860, species: 0 },
  },
  '3': {
    title: 'Centenary Oak Planting',
    description: 'Celebrating our 100th year with the planting of 100 native oak trees across Rutland and Lincolnshire. This centenary initiative creates lasting green legacies for future generations while marking a century of countryside protection.\n\nWe are seeking volunteers to help with planting days, aftercare, and recording tree health. Sites are selected to enhance biodiversity corridors and connect existing woodland habitats.\n\nEach oak planted represents our commitment to the countryside over the past hundred years and our vision for the next century of conservation.',
    location: 'Various Locations',
    county: 'Rutland',
    category: 'centenary',
    status: 'volunteer_needed',
    image_url: projectImage('11.jpg'),
    latitude: 52.6369,
    longitude: -0.7017,
    gallery: projectGallery(11, 12, 13, 14, 15),
    timeline: [
      { date: '2025-01', title: 'Centenary Launch', description: '100-tree planting programme announced' },
      { date: '2025-04', title: 'First Planting Days', description: 'Volunteer planting events across Rutland' },
      { date: '2025-10', title: 'More Volunteers Needed', description: 'Additional planting days scheduled' },
    ],
    resources: [
      { name: 'Planting Guide', url: '#', size: '1.1 MB', type: 'pdf' },
      { name: 'Centenary Overview', url: '#', size: '2.0 MB', type: 'pdf' },
    ],
    impact: { volunteers: 18, hours: 420, species: 0 },
  },
  '4': {
    title: 'Sustainable Farming Partnership',
    description: 'Working with local farmers to implement sustainable farming practices that protect soil health, reduce chemical inputs, and enhance biodiversity across South Lincolnshire.\n\nThis completed partnership brought together CPRE, agricultural advisers, and participating farms to trial hedgerow restoration, pollinator-friendly field margins, and improved water management. Farmers received practical support to adopt methods that benefit both productivity and the countryside.\n\nThe project demonstrated how sustainable land management can work at scale, with participating farms reporting healthier soils and increased wildlife on their land.',
    location: 'South Lincolnshire',
    county: 'Lincolnshire',
    category: 'farming',
    status: 'completed',
    image_url: projectImage('16.jpg'),
    latitude: 52.8945,
    longitude: -0.3567,
    gallery: [
      ...projectGallery(16, 17, 18, 19),
      projectImage('20.png'),
    ],
    timeline: [
      { date: '2022-04', title: 'Partnership Launched', description: 'Farmers recruited across South Lincolnshire' },
      { date: '2023-06', title: 'Hedgerow Programme', description: 'Field margin and hedgerow restoration completed' },
      { date: '2024-03', title: 'Project Completed', description: 'Final evaluation and best-practice guide published' },
    ],
    resources: [
      { name: 'Partnership Summary PDF', url: '#', size: '2.1 MB', type: 'pdf' },
      { name: 'Sustainable Farming Guide', url: '#', size: '1.6 MB', type: 'pdf' },
    ],
    impact: { volunteers: 12, hours: 540, species: 8 },
  },
  '5': {
    title: 'Opposing Overhead Pylon Infrastructure',
    description:
      'Lincolnshire faces plans for extensive overhead pylon lines and substations to connect offshore wind generation in the North Sea. CPRE Rutland and Lincolnshire is working with communities and decision makers to raise awareness of the landscape harm these proposals would cause across our open fenlands and coastal views.\n\nWe recognise the need for renewable energy but believe it must not come at the expense of nature, productive farmland, or the character of the county. Alternatives such as underground cabling should be fully considered before pylons are allowed to dominate the horizon.\n\nOur campaign brings together local voices to ensure infrastructure is sited and designed in ways that protect Lincolnshire\'s countryside for future generations.',
    location: 'Lincolnshire',
    county: 'Lincolnshire',
    category: 'planning',
    status: 'ongoing',
    image_url: projectImage('11.jpg'),
    latitude: 53.2,
    longitude: -0.4,
    gallery: projectGallery(11, 25, 8, 3, 27),
    timeline: [
      { date: '2025-01', title: 'Infrastructure Alert', description: 'Monitoring major pylon and substation proposals' },
      { date: '2025-06', title: 'Community Briefings', description: 'Public information sessions across affected areas' },
    ],
    resources: [
      { name: 'Pylon Proposal Briefing', url: '#', size: '1.4 MB', type: 'pdf' },
    ],
    impact: { volunteers: 28, hours: 640, species: 0 },
  },
  '6': {
    title: 'Solar Farm Agricultural Land Protection',
    description:
      'Reports suggest over 30,000 acres of mostly good-quality agricultural land in Lincolnshire could be covered by solar panels under plans approved or in consideration, with some arrays proposed up to four metres high.\n\nCPRE supports renewable energy but campaigns for rooftop solar on buildings and development on brownfield sites rather than the permanent loss of versatile farmland and open countryside. The efficiency and environmental sustainability of large ground-mounted schemes must be properly assessed.\n\nWe are working nationally and locally to ensure solar energy policy protects Lincolnshire\'s landscapes and food-producing land while still meeting climate goals.',
    location: 'Lincolnshire',
    county: 'Lincolnshire',
    category: 'planning',
    status: 'ongoing',
    image_url: projectImage('25.jpg'),
    latitude: 53.1,
    longitude: -0.2,
    gallery: projectGallery(25, 2, 7, 17, 8),
    timeline: [
      { date: '2024-09', title: 'Solar Farm Monitoring', description: 'Tracking major applications on agricultural land' },
      { date: '2025-03', title: 'Rooftop Solar Campaign', description: 'Promoting alternatives to greenfield solar' },
    ],
    resources: [
      { name: 'Solar on Rooftops Briefing', url: '#', size: '1.2 MB', type: 'pdf' },
    ],
    impact: { volunteers: 22, hours: 510, species: 0 },
  },
  '7': {
    title: 'Lincolnshire Wolds Landscape Preservation',
    description:
      'The Lincolnshire Wolds offer chalk downlands rising to over 500 feet at Normanby Hill, limestone heathlands, broad rivers, and a coastline rich in birdlife—far from the flat landscape many imagine.\n\nThis project protects the Wolds and wider county countryside from inappropriate development, including housing pressure and industrial schemes, while supporting initiatives that enhance beauty, access, and biodiversity.\n\nCPRE\'s renewed presence in Lincolnshire aims to bring communities together to safeguard the historic character, nature, and views—including landmarks such as Lincoln Cathedral visible from miles away—that make the county distinctive.',
    location: 'Lincolnshire Wolds',
    county: 'Lincolnshire',
    category: 'habitat',
    status: 'ongoing',
    image_url: projectImage('13.jpg'),
    latitude: 53.35,
    longitude: -0.12,
    gallery: projectGallery(13, 6, 31, 12, 14),
    timeline: [
      { date: '2025-02', title: 'Wolds Survey', description: 'Landscape and habitat priorities identified' },
      { date: '2025-08', title: 'Protection Advocacy', description: 'Responses to threats across the Wolds AONB' },
    ],
    resources: [
      { name: 'Wolds Landscape Guide', url: '#', size: '2.0 MB', type: 'pdf' },
    ],
    impact: { volunteers: 35, hours: 780, species: 18 },
  },
};

/** Gallery lives in frontend fallbacks; API/DB projects use UUID ids — match by title too. */
const resolveFallbackProject = (routeId, apiProject) => {
  if (FALLBACK_PROJECTS_BY_ID[routeId]) {
    return FALLBACK_PROJECTS_BY_ID[routeId];
  }
  const title = apiProject?.title;
  if (!title) return null;
  return (
    Object.values(FALLBACK_PROJECTS_BY_ID).find((project) => project.title === title) ??
    null
  );
};

const ProjectDetailPage = () => {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProject = async () => {
      try {
        const response = await axios.get(`${API}/projects/${id}`);
        const fallback = resolveFallbackProject(id, response.data);
        let data = { ...response.data, id };
        if (fallback) {
          if (['1', '2', '3', '4', '5', '6', '7'].includes(id) && fallback.image_url) {
            data.image_url = fallback.image_url;
          }
          if (fallback.gallery?.length) {
            data.gallery = fallback.gallery;
          }
        }
        setProject(data);
      } catch (error) {
        console.error('Failed to fetch project:', error);
        const fallback = FALLBACK_PROJECTS_BY_ID[id];
        setProject(fallback ? { ...fallback, id } : null);
      } finally {
        setLoading(false);
      }
    };
    fetchProject();
  }, [id]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Link copied to clipboard!');
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-custom">
            <div className="animate-pulse space-y-8">
              <div className="h-8 w-32 bg-stone-200 rounded" />
              <div className="h-96 bg-stone-200 rounded-2xl" />
              <div className="h-8 w-3/4 bg-stone-200 rounded" />
              <div className="h-4 w-full bg-stone-200 rounded" />
              <div className="h-4 w-full bg-stone-200 rounded" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="section-padding text-center">
          <h2>Project not found</h2>
          <Link to="/projects">
            <Button className="mt-4">Back to Projects</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const status = statusConfig[project.status] || statusConfig.ongoing;
  const StatusIcon = status.icon;

  return (
    <Layout>
      <div data-testid="project-detail-page">
        {/* Breadcrumbs */}
        <div className="container-custom pt-8">
          <Breadcrumbs customItems={[
            { label: 'Projects', href: '/projects' },
            { label: project.title }
          ]} />
        </div>

        {/* Hero Image */}
        <section className="pb-8">
          <div className="container-custom">
            <div className="relative aspect-[21/9] rounded-2xl overflow-hidden">
              <img
                src={project.image_url || '/images/cpre images for website/23.jpg'}
                alt={project.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
              <div className="absolute bottom-8 left-8 right-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                  <Badge className={`${status.color} rounded-full text-sm font-medium mb-4`}>
                    <StatusIcon className="w-4 h-4 mr-1" />
                    {status.label}
                  </Badge>
                  <h1 className="text-white text-3xl sm:text-4xl lg:text-5xl font-heading font-bold">
                    {project.title}
                  </h1>
                  <div className="flex items-center gap-2 text-white/80 mt-2">
                    <MapPin className="w-4 h-4" />
                    <span>{project.location}, {project.county}</span>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button variant="secondary" size="sm" onClick={handleShare} className="rounded-full" data-testid="share-btn">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  <Link to="/donate">
                    <Button size="sm" className="bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 rounded-full" data-testid="support-btn">
                      <Heart className="w-4 h-4 mr-2" />
                      Support
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Main Content - Organized Sections */}
        <section className="pb-16">
          <div className="container-custom">
            {/* Quick Stats Bar */}
            {project.impact && (
              <div className="grid grid-cols-3 gap-4 mb-12">
                <Card className="bg-cpre-new-yew/5 border-0">
                  <CardContent className="p-6 text-center">
                    <span className="block text-3xl font-heading font-bold text-cpre-new-yew">{project.impact.volunteers}</span>
                    <span className="text-sm text-muted-foreground">Volunteers</span>
                  </CardContent>
                </Card>
                <Card className="bg-cpre-autumn-leaf/5 border-0">
                  <CardContent className="p-6 text-center">
                    <span className="block text-3xl font-heading font-bold text-cpre-autumn-leaf">{project.impact.hours}</span>
                    <span className="text-sm text-muted-foreground">Hours Contributed</span>
                  </CardContent>
                </Card>
                <Card className="bg-cpre-hill/5 border-0">
                  <CardContent className="p-6 text-center">
                    <span className="block text-3xl font-heading font-bold text-cpre-hill">{project.impact.species}</span>
                    <span className="text-sm text-muted-foreground">Species Protected</span>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* 1. SUMMARY SECTION */}
            <div className="mb-16" data-testid="project-summary">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-cpre-new-yew rounded-full" />
                <h2 className="text-2xl font-heading font-semibold">Summary</h2>
              </div>
              <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2">
                    <div className="prose prose-stone max-w-none">
                      {project.description.split('\n\n').map((paragraph, index) => (
                        <p key={index} className="text-muted-foreground leading-relaxed mb-4 last:mb-0">
                          {paragraph}
                        </p>
                      ))}
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-cpre-pebble/30 rounded-xl p-4">
                      <span className="text-sm text-muted-foreground">Location</span>
                      <p className="font-medium flex items-center gap-2 mt-1">
                        <MapPin className="w-4 h-4 text-cpre-new-yew" />
                        {project.location}
                      </p>
                    </div>
                    <div className="bg-cpre-pebble/30 rounded-xl p-4">
                      <span className="text-sm text-muted-foreground">County</span>
                      <p className="font-medium mt-1">{project.county}</p>
                    </div>
                    <div className="bg-cpre-pebble/30 rounded-xl p-4">
                      <span className="text-sm text-muted-foreground">Category</span>
                      <p className="font-medium capitalize mt-1">{project.category?.replace('_', ' ')}</p>
                    </div>
                    <div className="bg-cpre-pebble/30 rounded-xl p-4">
                      <span className="text-sm text-muted-foreground">Status</span>
                      <div className="mt-1">
                        <Badge className={`${status.color}`}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 2. GALLERY SECTION */}
            {project.gallery && project.gallery.length > 0 && (
              <div className="mb-16" data-testid="project-gallery">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-cpre-hill rounded-full" />
                  <h2 className="text-2xl font-heading font-semibold">Gallery</h2>
                </div>
                <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm">
                  <ImageGallery images={project.gallery} title={project.title} />
                </div>
              </div>
            )}

            {/* 3. TIMELINE SECTION */}
            {project.timeline && project.timeline.length > 0 && (
              <div className="mb-16" data-testid="project-timeline">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-cpre-harvest-gold rounded-full" />
                  <h2 className="text-2xl font-heading font-semibold">Timeline</h2>
                </div>
                <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm">
                  <div className="relative pl-8 border-l-2 border-cpre-new-yew/20 space-y-8">
                    {project.timeline.map((item, index) => (
                      <div key={index} className="relative" data-testid={`timeline-${index}`}>
                        <div className="absolute -left-[41px] w-4 h-4 bg-cpre-new-yew rounded-full border-4 border-white shadow-sm" />
                        <div className="bg-cpre-pebble/20 rounded-xl p-4 ml-4">
                          <span className="inline-block px-3 py-1 bg-cpre-harvest-gold/20 text-cpre-new-yew text-sm font-medium rounded-full mb-2">{item.date}</span>
                          <h4 className="font-heading font-semibold text-lg">{item.title}</h4>
                          <p className="text-muted-foreground text-sm mt-1">{item.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* 4. VOLUNTEER CTA SECTION - Always visible */}
            <div className="mb-16" data-testid="project-volunteer-cta">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-1 h-8 bg-cpre-autumn-leaf rounded-full" />
                <h2 className="text-2xl font-heading font-semibold">Get Involved</h2>
              </div>
              <div className="bg-cpre-new-yew rounded-2xl p-8 text-white">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                  <div>
                    <Users className="w-16 h-16 mb-4 text-cpre-mint" />
                    <h3 className="font-heading font-bold text-2xl mb-3 text-white">Volunteer With Us</h3>
                    <p className="text-white/80 leading-relaxed mb-6">
                      Join our dedicated team of volunteers and help make a real difference to this project. 
                      Whether you can spare a few hours or want to commit to regular sessions, there's a role for everyone.
                    </p>
                    <ul className="space-y-2 text-white/80 mb-6">
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-cpre-mint" />
                        No experience necessary
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-cpre-mint" />
                        Training provided
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-cpre-mint" />
                        Flexible time commitment
                      </li>
                    </ul>
                  </div>
                  <div className="text-center md:text-right">
                    <Link to="/volunteering">
                      <Button size="lg" className="bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-full px-8 h-14 text-lg shadow-lg" data-testid="volunteer-cta-btn">
                        <Users className="w-5 h-5 mr-2" />
                        Become a Volunteer
                      </Button>
                    </Link>
                    <p className="text-white/60 text-sm mt-4">
                      Or contact us at volunteers@cpre-rutlincs.org.uk
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* 5. DOWNLOADABLE RESOURCES SECTION */}
            {project.resources && project.resources.length > 0 && (
              <div className="mb-16" data-testid="project-resources">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-cpre-mint rounded-full" />
                  <h2 className="text-2xl font-heading font-semibold">Downloadable Resources</h2>
                </div>
                <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {project.resources.map((resource, index) => (
                      <a
                        key={index}
                        href={resource.url}
                        className="flex items-center gap-4 p-4 rounded-xl bg-cpre-pebble/30 hover:bg-cpre-pebble/50 transition-colors duration-200 group border border-transparent hover:border-cpre-new-yew/20"
                        data-testid={`resource-${index}`}
                        download
                      >
                        <div className="w-12 h-12 bg-cpre-new-yew/10 rounded-xl flex items-center justify-center group-hover:bg-cpre-new-yew group-hover:text-white transition-colors duration-200">
                          <FileText className="w-6 h-6 text-cpre-new-yew group-hover:text-white" />
                        </div>
                        <div className="flex-1">
                          <span className="font-medium text-foreground group-hover:text-cpre-new-yew transition-colors duration-200 block">{resource.name}</span>
                          {resource.size && (
                            <span className="text-xs text-muted-foreground">{resource.size}</span>
                          )}
                        </div>
                        <Download className="w-5 h-5 text-cpre-new-yew opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Location Map */}
            {project.latitude && project.longitude && (
              <div data-testid="project-location">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-1 h-8 bg-cpre-pebble rounded-full" />
                  <h2 className="text-2xl font-heading font-semibold">Location</h2>
                </div>
                <div className="bg-white rounded-2xl p-8 border border-stone-100 shadow-sm">
                  <div className="aspect-video rounded-xl overflow-hidden border border-stone-200">
                    <iframe
                      title="Project Location"
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      scrolling="no"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${project.longitude - 0.05},${project.latitude - 0.03},${project.longitude + 0.05},${project.latitude + 0.03}&layer=mapnik&marker=${project.latitude},${project.longitude}`}
                    />
                  </div>
                  <a 
                    href={`https://www.openstreetmap.org/?mlat=${project.latitude}&mlon=${project.longitude}#map=14/${project.latitude}/${project.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-sm text-cpre-new-yew hover:underline mt-4"
                  >
                    <ExternalLink className="w-4 h-4" />
                    View larger map
                  </a>
                </div>
              </div>
            )}

            {/* Support CTA at bottom */}
            <div className="mt-16 text-center">
              <Card className="border-cpre-autumn-leaf/20 bg-gradient-to-r from-cpre-autumn-leaf/5 to-cpre-harvest-gold/5 inline-block">
                <CardContent className="p-8">
                  <Heart className="w-12 h-12 text-cpre-autumn-leaf mx-auto mb-4" />
                  <h3 className="font-heading font-semibold text-xl mb-2">Support This Project</h3>
                  <p className="text-muted-foreground mb-6 max-w-md">
                    Your donation directly funds conservation work and helps us protect the countryside for future generations.
                  </p>
                  <Link to="/donate">
                    <Button size="lg" className="bg-cpre-autumn-leaf hover:bg-cpre-autumn-leaf/90 text-white rounded-full px-8">
                      <Heart className="w-5 h-5 mr-2" />
                      Donate Now
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default ProjectDetailPage;
